<template>
  <div class="blogger-page">
    <van-nav-bar
      title="博主主页"
      left-arrow fixed placeholder
      @click-left="$router.back()"
    />

    <!-- Blogger info header -->
    <div class="blogger-header">
      <div class="blogger-avatar">
        <img v-if="bloggerAvatar" :src="bloggerAvatar" alt="" />
        <van-icon v-else name="user-o" size="34" color="#fff" />
      </div>
      <h3>{{ bloggerNickname || bloggerUsername || '加载中...' }}</h3>
      <p v-if="bloggerBio" class="blogger-bio">{{ bloggerBio }}</p>
      <p class="blogger-res-count">{{ list.length }} 个资源</p>

      <div class="follow-btn-wrap">
        <van-button
          v-if="!isFollowing"
          round size="small"
          color="#f59e0b"
          icon="add-o"
          @click="onFollow"
        >
          关注
        </van-button>
        <van-button
          v-else
          round size="small"
          color="rgba(255, 255, 255, 0.25)"
          icon="success"
        >
          已关注
        </van-button>
      </div>
    </div>

    <!-- Only followers can see resources -->
    <template v-if="isFollowing">
      <van-search
        v-model="keyword"
        placeholder="搜索资源"
        shape="round"
        background="#f5f7fa"
        @search="onSearch"
      />

      <van-tabs
        v-model="filterPaid"
        @change="onFilterChange"
        color="#6366f1"
        title-active-color="#6366f1"
        sticky
      >
        <van-tab title="全部" name="" />
        <van-tab title="免费" name="0" />
        <van-tab title="付费" name="1" />
      </van-tabs>

      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          finished-text="— 没有更多了 —"
          @load="onLoad"
        >
          <div
            v-for="item in list" :key="item.id"
            class="feed-card"
          >
            <div class="feed-content" @click="goDetail(item)">
              <h3 class="feed-title">{{ item.title }}</h3>
              <p class="feed-desc" v-if="item.description">{{ item.description }}</p>
              <div v-if="item.coverUrl" class="feed-cover" @click.stop="previewImage(item.coverUrl)">
                <img :src="item.coverUrl" alt="" />
              </div>
            </div>
            <div class="feed-actions">
              <span class="feed-time">{{ formatDate(item.createdAt) }}</span>
              <span class="tag" :class="item.isPaid ? 'paid' : 'free'">
                {{ item.isPaid ? '¥' + item.price : '免费' }}
              </span>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>

      <van-empty
        v-if="!loading && list.length === 0"
        image="search" description="该博主暂无资源"
      />
    </template>

    <!-- Not following: show prompt -->
    <div v-else-if="checkedFollow" class="follow-prompt">
      <van-icon name="lock" size="48" color="#d1d5db" />
      <p>关注后可查看该博主资源</p>
      <van-button round color="#f59e0b" icon="add-o" @click="onFollow">立即关注</van-button>
    </div>
  </div>
</template>

<script>
import { resourceApi, followApi } from '../api';
import { ImagePreview } from 'vant';

export default {
  data() {
    return {
      keyword: '', filterPaid: '', list: [],
      page: 1, size: 10, loading: false, finished: false,
      refreshing: false,
      bloggerUsername: '', bloggerAvatar: '',
      bloggerNickname: '', bloggerBio: '',
      isFollowing: false, checkedFollow: false
    };
  },
  created() {
    this.fetchProfile();
    this.checkAutoFollow();
  },
  methods: {
    async fetchProfile() {
      try {
        const bloggerId = this.$route.params.id;
        const res = await resourceApi.listByBlogger(bloggerId, { page: 1, size: 1 });
        const blogger = res.data.blogger;
        if (blogger) {
          this.bloggerUsername = blogger.bloggerName;
          this.bloggerAvatar = blogger.bloggerAvatar;
          this.bloggerNickname = blogger.bloggerNickname;
          this.bloggerBio = blogger.bloggerBio;
        }
      } catch (e) { /* ignore */ }
    },
    async checkFollowStatus() {
      if (!this.$store.state.token) {
        this.checkedFollow = true;
        return;
      }
      try {
        const res = await followApi.myFollowings({ page: 1, size: 100 });
        const bloggerId = parseInt(this.$route.params.id);
        this.isFollowing = res.data.list.some(f => f.bloggerId === bloggerId);
      } catch (e) { /* ignore */ }
      this.checkedFollow = true;
    },
    async checkAutoFollow() {
      // Auto-follow after login/register redirect
      const autoFollow = this.$route.query.autoFollow;
      if (autoFollow && this.$store.state.token) {
        const bloggerId = parseInt(autoFollow);
        try {
          await followApi.follow(bloggerId);
          this.isFollowing = true;
          this.$toast.success('已自动关注该博主');
          // Clean query
          this.$router.replace({ query: {} });
        } catch (e) { /* ignore */ }
      } else {
        this.checkFollowStatus();
      }
    },
    async onFollow() {
      if (!this.$store.state.token) {
        const bloggerId = this.$route.params.id;
        this.$router.push({
          name: 'Login',
          query: { redirect: '/blogger/' + bloggerId, autoFollow: bloggerId }
        });
        return;
      }
      try {
        await followApi.follow(this.$route.params.id);
        this.isFollowing = true;
        this.$toast.success('关注成功');
      } catch (e) { /* ignore */ }
    },
    async onUnfollow() {
      try {
        await followApi.unfollow(this.$route.params.id);
        this.isFollowing = false;
        this.$toast.success('已取消关注');
      } catch (e) { /* ignore */ }
    },
    async onLoad() { await this.fetchList(); },
    async onRefresh() {
      this.page = 1; this.finished = false; this.list = [];
      this.loading = true;
      await this.fetchList();
      this.refreshing = false;
    },
    async onSearch() {
      this.page = 1; this.finished = false; this.list = [];
      this.loading = true;
      await this.fetchList();
    },
    async onFilterChange() {
      this.page = 1; this.finished = false; this.list = [];
      this.loading = true;
      await this.fetchList();
    },
    async fetchList() {
      const bloggerId = this.$route.params.id;
      const res = await resourceApi.listByBlogger(bloggerId, {
        page: this.page, size: this.size,
        keyword: this.keyword || undefined,
        isPaid: this.filterPaid || undefined
      });
      const data = res.data;
      this.list = this.page === 1 ? data.list : [...this.list, ...data.list];
      this.loading = false;
      if (this.list.length >= data.total) this.finished = true;
      this.page++;
    },
    goDetail(item) {
      this.$router.push('/resource/' + item.id);
    },
    previewImage(url) {
      ImagePreview([url]);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }
  }
};
</script>

<style scoped>
.blogger-page { min-height: 100vh; background: #f5f7fa; }
.blogger-header {
  text-align: center; padding: 28px 20px 22px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff; position: relative;
}
.blogger-avatar {
  width: 68px; height: 68px; border-radius: 50%;
  background: rgba(255,255,255,0.2); overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 12px;
}
.blogger-avatar img { width: 100%; height: 100%; object-fit: cover; }
.blogger-header h3 { font-size: 20px; font-weight: 700; margin: 0; }
.blogger-bio {
  font-size: 13px; opacity: 0.85; margin: 6px 0 0;
  line-height: 1.4; max-width: 280px; margin-left: auto; margin-right: auto;
}
.blogger-res-count { font-size: 13px; opacity: 0.7; margin: 6px 0 0; }
.follow-btn-wrap {
  margin-top: 14px;
}

/* Feed card */
.feed-card {
  margin: 8px 16px; background: #fff;
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}
.feed-content { padding: 14px 14px 0; cursor: pointer; }
.feed-title {
  font-size: 16px; font-weight: 700; color: #1f2937; margin: 0;
}
.feed-desc {
  font-size: 14px; color: #6b7280; line-height: 1.5; margin: 6px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.feed-cover {
  margin-top: 10px; border-radius: 10px; overflow: hidden;
  background: #f1f5f9; max-height: 320px;
}
.feed-cover img { width: 100%; max-height: 320px; object-fit: cover; display: block; }
.feed-actions {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px;
}
.feed-time { font-size: 11px; color: #d1d5db; }
.tag {
  padding: 3px 10px; border-radius: 10px;
  font-size: 12px; font-weight: 600; color: #fff;
}
.tag.free { background: #10b981; }
.tag.paid { background: #ef4444; }

.follow-prompt {
  text-align: center; padding: 80px 40px; color: #9ca3af;
}
.follow-prompt p {
  font-size: 15px; margin: 20px 0 24px; color: #6b7280;
}
</style>
